import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const UNCATEGORISED = "Other";

function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
}

export default function Catalog() {
  const { user } = useAuth();
  const isLoggedIn = !!(user && user.role);
  const isGuest = user === false;
  const [products, setProducts] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    if (user === null) return;
    const endpoint = isLoggedIn ? "/products" : "/products/public";
    api.get(endpoint).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [user, isLoggedIn]);

  // Categories present among products (sorted A-Z, "Other" last)
  const categoryTabs = useMemo(() => {
    if (!products) return [];
    const set = new Set(products.map((p) => (p.category && p.category.trim()) || UNCATEGORISED));
    const arr = [...set].sort((a, b) => {
      if (a === UNCATEGORISED) return 1;
      if (b === UNCATEGORISED) return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return arr;
  }, [products]);

  // Filter by search + active category, then group by category
  const groups = useMemo(() => {
    if (!products) return null;
    const q = query.trim().toLowerCase();
    const filtered = products.filter((p) => {
      const cat = (p.category && p.category.trim()) || UNCATEGORISED;
      const matchQ = !q || p.name?.toLowerCase().includes(q);
      const matchCat = activeCat === "all" || cat === activeCat;
      return matchQ && matchCat;
    });
    const byCat = {};
    filtered.forEach((p) => {
      const cat = (p.category && p.category.trim()) || UNCATEGORISED;
      (byCat[cat] = byCat[cat] || []).push(p);
    });
    const orderedCats = Object.keys(byCat).sort((a, b) => {
      if (a === UNCATEGORISED) return 1;
      if (b === UNCATEGORISED) return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return orderedCats.map((cat) => ({
      cat,
      items: byCat[cat].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })),
    }));
  }, [products, query, activeCat]);

  const totalVisible = groups ? groups.reduce((n, g) => n + g.items.length, 0) : 0;

  const renderCard = (p) => {
    const inner = (
      <>
        <div className="aspect-square bg-gray-50 overflow-hidden flex items-center justify-center">
          {p.image_url
            ? <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover"/>
            : <span className="text-xs text-gray-300">No image</span>}
        </div>
        <div className="p-3">
          <div className="font-heading font-medium text-sm text-gray-900 line-clamp-1">{p.name}</div>
          {isLoggedIn ? (
            <div className="mt-2 flex items-center justify-between">
              <div className="text-sm font-medium text-[#002fa7]">{inr(p.base_price)}</div>
              <div className="text-[11px] text-gray-400">{(p.variants || []).length} var.</div>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-400">
              <Lock className="w-3 h-3" strokeWidth={1.6}/> Sign in for price
            </div>
          )}
        </div>
      </>
    );
    return isLoggedIn ? (
      <Link key={p.id} to={`/products/${p.id}`} data-testid={`product-card-${p.id}`}
        className="border border-gray-200 hover:-translate-y-1 hover:shadow-sm transition-all duration-200 bg-white">
        {inner}
      </Link>
    ) : (
      <div key={p.id} data-testid={`product-card-${p.id}`} className="border border-gray-200 bg-white">
        {inner}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-medium">Catalog</p>
            <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight" data-testid="catalog-title">Our products</h1>
            <p className="text-sm text-gray-500 mt-2">
              {isLoggedIn
                ? "Pick a product and configure variants to add to your cart."
                : "Browse our range of hospitality & housekeeping supplies."}
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products by name…" className="rounded-sm pl-9" data-testid="catalog-search"/>
          </div>
        </div>

        {isGuest && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#0e5a32]/20 bg-[#0e5a32]/5 px-5 py-4 rounded-md" data-testid="catalog-guest-banner">
            <div className="flex items-center gap-2 text-sm text-[#0e5a32]">
              <Lock className="w-4 h-4 shrink-0" strokeWidth={1.6} />
              Sign in to see prices and place orders.
            </div>
            <div className="flex gap-2">
              <Link to="/login"><Button size="sm" className="rounded-sm bg-[#002fa7] hover:bg-[#00227b]" data-testid="catalog-guest-signin">Sign in</Button></Link>
              <Link to="/register"><Button size="sm" variant="outline" className="rounded-sm border-gray-300" data-testid="catalog-guest-register">Create account</Button></Link>
            </div>
          </div>
        )}

        {/* Category filter */}
        {categoryTabs.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="catalog-category-filter">
            <button onClick={() => setActiveCat("all")} data-testid="catalog-cat-all"
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeCat === "all" ? "bg-[#0e5a32] text-white border-[#0e5a32]" : "bg-white text-gray-600 border-gray-300 hover:border-[#0e5a32]"}`}>
              All
            </button>
            {categoryTabs.map((c) => (
              <button key={c} onClick={() => setActiveCat(c)} data-testid={`catalog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeCat === c ? "bg-[#0e5a32] text-white border-[#0e5a32]" : "bg-white text-gray-600 border-gray-300 hover:border-[#0e5a32]"}`}>
                {c}
              </button>
            ))}
          </div>
        )}

        {groups === null ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="border border-gray-200 animate-pulse">
                <div className="aspect-square bg-gray-100"/>
                <div className="p-3 space-y-2"><div className="h-3 bg-gray-100 w-3/4"/><div className="h-3 bg-gray-100 w-1/2"/></div>
              </div>
            ))}
          </div>
        ) : totalVisible === 0 ? (
          <div className="border border-gray-200 p-12 text-center text-sm text-gray-500" data-testid="catalog-empty">
            No products found.
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map((g) => (
              <div key={g.cat} data-testid={`catalog-group-${g.cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  {g.cat} <span className="text-sm font-normal text-gray-400">({g.items.length})</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {g.items.map(renderCard)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
